/**
 * Storage Adapter Interface and Implementations
 */

import { NotificationPayload } from '../types';

export interface StorageAdapter {
  save(notification: NotificationPayload): Promise<NotificationPayload>;
  find(filters: any): Promise<NotificationPayload[]>;
  findOne(id: string): Promise<NotificationPayload | null>;
  update(id: string, updates: Partial<NotificationPayload>): Promise<NotificationPayload | null>;
  delete(id: string): Promise<boolean>;
  count(filters?: any): Promise<number>;
}

export class InMemoryStorageAdapter implements StorageAdapter {
  private notifications: Map<string, NotificationPayload> = new Map();

  async save(notification: NotificationPayload): Promise<NotificationPayload> {
    this.notifications.set(notification.id, notification);
    return notification;
  }

  async find(filters: any = {}): Promise<NotificationPayload[]> {
    let notifications = Array.from(this.notifications.values());

    if (filters.recipientId) {
      notifications = notifications.filter(n => n.recipientId === filters.recipientId);
    }

    if (filters.read !== undefined) {
      notifications = notifications.filter(n => n.read === filters.read);
    }

    if (filters.type) {
      notifications = notifications.filter(n => n.type === filters.type);
    }

    if (filters.limit) {
      notifications = notifications.slice(0, filters.limit);
    }

    return notifications;
  }

  async findOne(id: string): Promise<NotificationPayload | null> {
    return this.notifications.get(id) || null;
  }

  async update(id: string, updates: Partial<NotificationPayload>): Promise<NotificationPayload | null> {
    const notification = this.notifications.get(id);
    if (!notification) return null;

    const updated = { ...notification, ...updates };
    this.notifications.set(id, updated);
    return updated;
  }

  async delete(id: string): Promise<boolean> {
    return this.notifications.delete(id);
  }

  async count(filters: any = {}): Promise<number> {
    const notifications = await this.find(filters);
    return notifications.length;
  }
}
